import { Card, CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Cloud,
  Trash2,
  Zap,
  Recycle,
  TrendingUp,
  TrendingDown,
  Info,
  Sprout,
} from "lucide-react";
import type { ImpactKPIJadlog } from "@/hooks/useImpactDataJadlog";
import { IMPACT_METRIC_COLORS_JADLOG } from "@/utils/impactMetricColorsJadlog";

interface ImpactKPICardsJadlogProps {
  kpis: ImpactKPIJadlog[];
}

const KPI_ICONS_JADLOG: Record<string, React.ElementType> = {
  cloud: Cloud,
  trash: Trash2,
  zap: Zap,
  recycle: Recycle,
  sprout: Sprout,
};

const KPI_COLORS_JADLOG: Record<string, string> = {
  co2: IMPACT_METRIC_COLORS_JADLOG.co2,
  landfill: IMPACT_METRIC_COLORS_JADLOG.landfill,
  energy: IMPACT_METRIC_COLORS_JADLOG.energy,
  recovered: IMPACT_METRIC_COLORS_JADLOG.recovered,
  fertilizer: "#22C55E", // Verde para fertilizante
};

const formatValue = (value: number, unit: string): string => {
  if (unit === "m³") {
    return value.toLocaleString("pt-BR", {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
  }
  return new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(
    value,
  );
};

export const ImpactKPICardsJadlog = ({ kpis }: ImpactKPICardsJadlogProps) => {
  // Filtrar apenas KPIs com valor > 0 e ordenar conforme especificado
  const orderedIds = ["co2", "landfill", "recovered", "energy", "fertilizer"];
  const validKpis = kpis.filter(
    (kpi) => orderedIds.includes(kpi.id) && kpi.value > 0,
  );

  // Ordenar conforme a ordem desejada
  const sortedKpis = validKpis.sort(
    (a, b) => orderedIds.indexOf(a.id) - orderedIds.indexOf(b.id),
  );

  const topRow = sortedKpis.slice(0, 3);
  const bottomRow = sortedKpis.slice(3);

  const renderKPICard = (kpi: ImpactKPIJadlog) => {
    const IconComponent = KPI_ICONS_JADLOG[kpi.icon] || Cloud;
    const color = KPI_COLORS_JADLOG[kpi.id] || "#10B981";

    return (
      <Card
        key={kpi.id}
        className="bg-white border-border shadow-md hover:shadow-lg transition-shadow"
      >
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-gray-600">{kpi.label}</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-gray-500 cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>{kpi.tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="flex items-center gap-3">
            <div
              className="p-2.5 rounded-lg"
              style={{ backgroundColor: `${color}15` }}
            >
              <IconComponent className="h-5 w-5" style={{ color }} />
            </div>
            <div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-bold" style={{ color }}>
                  {formatValue(kpi.value, kpi.unit)}
                </span>
                <span className="text-sm text-gray-600">{kpi.unit}</span>
              </div>
              {kpi.change !== undefined && (
                <div
                  className={`flex items-center gap-1 ${
                    kpi.change >= 0 ? "text-emerald-600" : "text-red-500"
                  }`}
                >
                  {kpi.change >= 0 ? (
                    <TrendingUp className="h-3 w-3" />
                  ) : (
                    <TrendingDown className="h-3 w-3" />
                  )}
                  <span className="text-xs font-medium">
                    {kpi.change >= 0 ? "+" : ""}
                    {kpi.change}% vs período anterior
                  </span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4">
      {/* Top row - 3 cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <TooltipProvider>{topRow.map(renderKPICard)}</TooltipProvider>
      </div>

      {/* Bottom row - remaining cards */}
      {bottomRow.length > 0 && (
        <div
          className={`grid grid-cols-1 ${bottomRow.length === 1 ? "md:grid-cols-1 max-w-md" : "md:grid-cols-2"} gap-4`}
        >
          <TooltipProvider>{bottomRow.map(renderKPICard)}</TooltipProvider>
        </div>
      )}
    </div>
  );
};
